'use client';

/**
 * Schema Explorer - Schema-driven explorer panel renderer
 *
 * @component_role Generic explorer panel renderer
 * @business_rule Reads the current mode from the layout store, looks up the
 *   explorer schema via useExplorerSchema, iterates the sections in order
 *   and dispatches each to its concrete section component based on the
 *   section type. Mirrors the copilot DetailsPanel pattern.
 *
 *   Adding a section to an explorer is a backend config change in
 *   entity_schema.py:EXPLORER_SCHEMA. Adding a new section *type* requires
 *   wiring a new case in SECTION_COMPONENTS below + a new enum value on
 *   both sides (Python and TypeScript). No changes required to this file
 *   to add new sections to existing section types.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain explorer
 */

import { Fragment, type ComponentType, type ReactNode } from 'react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import {
  useExplorerSchema,
  useExplorerLabels,
  useTotalMatrixStore,
} from '@/lib/stores/total-matrix-store';
import type {
  ExplorerSectionConfig,
  ExplorerSectionType,
  LabelsDict,
} from '@/lib/api/types';
import {
  IntentComposerSection,
  AddProductActionSection,
  ProductsBrowseSection,
  IntentHistorySection,
  IntentSchedulesSection,
  IntentAddScheduleSection,
  StyleGuidanceSection,
  SearchBarSection,
  ProjectCardSection,
  ClarificationSection,
  SearchResultsSection,
  EmptyStateSection,
} from './sections';
import { ObeyaModeToggleSection } from './sections/obeya-mode-toggle-section';
import { ObeyaProjectsListSection } from './sections/obeya-projects-list-section';
import { ObeyaExplorerProvider } from './providers/obeya-explorer-provider';

type SectionComponent = ComponentType<{
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}>;

/**
 * Dispatch map: ExplorerSectionType → concrete React component.
 *
 * Discovery mode uses: discovery_composer, add_product_action,
 * products_browse, conversation_history, style_guidance.
 *
 * Other modes (projects, vendors, obeya, docs, agents, testing, rfi, vendor
 * portal) are still rendered by their bespoke explorer components via the
 * EXPLORER_COMPONENTS map in explorer.tsx. When those migrate to schema-
 * driven rendering, add their section type renderers here (search_bar,
 * project_card, search_results, clarification, empty_state, entity_list,
 * tree_view are all currently unimplemented).
 */
const SECTION_COMPONENTS: Partial<Record<ExplorerSectionType, SectionComponent>> = {
  intent_composer: IntentComposerSection,
  add_product_action: AddProductActionSection,
  products_browse: ProductsBrowseSection,
  intent_history: IntentHistorySection,
  intent_schedules: IntentSchedulesSection,
  intent_add_schedule: IntentAddScheduleSection,
  style_guidance: StyleGuidanceSection,
  // Generic narrative-search primitives — wired to ExplorerSearchProvider.
  // Used by agents + obeya simulations; future modes opt in by
  // providing the context.
  search_bar: SearchBarSection,
  project_card: ProjectCardSection,
  search_results: SearchResultsSection,
  clarification: ClarificationSection,
  empty_state: EmptyStateSection,
  // Obeya-specific
  obeya_mode_toggle: ObeyaModeToggleSection,
  obeya_projects_list: ObeyaProjectsListSection,
  // Not yet implemented — modes still using bespoke explorers:
  //   entity_list, tree_view
};

/**
 * Per-mode wrapper providers — modes that need shared cross-section
 * state (e.g. obeya's sub-mode toggle, agents' ki_ask flow) declare
 * a provider here. SchemaExplorer wraps the section list in the
 * registered component so all sections under that mode see the
 * provided context. Modes without a provider use Fragment (no-op).
 *
 * Mirrors the pattern used by `<ChatSurfaceProvider />` in the chat
 * surface — per-mount shared state, not global.
 */
const PROVIDER_REGISTRY: Record<string, ComponentType<{ children: ReactNode }>> = {
  obeya: ObeyaExplorerProvider,
};

function ExplorerSectionRenderer({
  config,
  labels,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  const Component = SECTION_COMPONENTS[config.type];

  if (!Component) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[SchemaExplorer] No renderer registered for section type: ${config.type}`,
      );
    }
    return null;
  }

  return <Component config={config} labels={labels} />;
}

export function SchemaExplorer() {
  const mode = useLayoutStore((s) => s.activeExplorerView);
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  // The explorer schema/provider are keyed by *strategy* — what the
  // explorer panel renders — not by mode. Multiple modes can share an
  // explorer strategy (e.g. mode1 reuses `obeya`'s tree); they all
  // resolve through this lookup so they get the same sections + the
  // same wrapping provider.
  const cfg = getViewportConfig(mode, null);
  const strategyKey = cfg?.explorer?.strategy ?? mode;

  const schema = useExplorerSchema(strategyKey);
  const labels = useExplorerLabels(strategyKey);

  // Schema hasn't loaded yet (total matrix still being fetched)
  if (!schema) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        Loading explorer…
      </div>
    );
  }

  const Provider = PROVIDER_REGISTRY[strategyKey] ?? Fragment;

  return (
    <Provider>
      <div className="flex flex-col">
        {schema.sections.map((section, idx) => (
          <ExplorerSectionRenderer
            key={`${section.type}-${idx}`}
            config={section}
            labels={labels}
          />
        ))}
      </div>
    </Provider>
  );
}
